import { useState, useEffect, useMemo } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "src/components/ui/Card";
import { Button } from "src/components/ui/Button";
import { Input } from "src/components/ui/Input";
import { Badge } from "src/components/ui/Badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "src/components/ui/Select";
import { Star, Search, Clock, Calendar, MapPin, Filter, Loader2, BookOpen } from "lucide-react";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "src/components/ui/Tabs";
import { Link } from "react-router-dom";
import TutorProfileModal from "src/components/tutor/ProfileModal";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "src/components/ui/Tooltip";
import AuthenticatedImage from "src/components/ui/AuthenticatedImage";
import useScrollToTop from "src/hooks/useScrollToTop";
import { useSearchTutors, Tutor, TutorSearchFilters } from "src/services/tutorsService";

export const getTutorEducation = (tutor: Tutor) => {
  if (tutor.education && tutor.education.length > 0) {
    const firstEducation = tutor.education[0];
    return `${firstEducation.degree || ''} in ${firstEducation.field || ''}, ${firstEducation.institution || ''}`.replace(/, $/, '');
  }
  return "Education not specified";
};

const TutorSearch = () => {
  const [searchTerm, setSearchTerm] = useState("");
  const [specialty, setSpecialty] = useState("all");
  const [availability, setAvailability] = useState("any");
  const [selectedTutor, setSelectedTutor] = useState<Tutor | null>(null);
  const [showProfileModal, setShowProfileModal] = useState(false);

  useScrollToTop();

  // Create search filters object - only recreate when actual filter values change
  const searchFilters: TutorSearchFilters = useMemo(() => {
    const filters: TutorSearchFilters = {};
    
    if (searchTerm && searchTerm.trim()) {
      filters.searchTerm = searchTerm.trim();
    }
    
    if (specialty && specialty !== "all") {
      filters.specialty = specialty;
    }
    
    if (availability && availability !== "any") {
      filters.availability = availability;
    }
    
    return filters;
  }, [searchTerm, specialty, availability]);

  // Fetch tutors using React Query
  const { tutors = [], isLoading, error } = useSearchTutors(searchFilters);

  // Handle opening the tutor profile modal
  const handleOpenTutorProfile = (tutor: Tutor) => {
    setSelectedTutor(tutor);
    setShowProfileModal(true);
    console.log("Opening modal for tutor:", `${tutor.first_name} ${tutor.last_name}`);
  };

  // Handle closing the tutor profile modal
  const handleCloseModal = () => {
    setShowProfileModal(false);
    // Don't clear selectedTutor immediately to avoid unnecessary re-renders
    // It will be cleared when a new tutor is selected
  };

  // Get unique specialties for filter dropdown from fetched tutors
  const uniqueSpecialties = Array.from(
    new Set(tutors.flatMap((tutor) => tutor.subjects_taught || []))
  );

  // Helper function to get tutor's full name
  const getTutorName = (tutor: Tutor) => `${tutor.first_name} ${tutor.last_name}`;

  // Helper function to get tutor's experience in years
  const getTutorExperience = (tutor: Tutor) => {
    if (tutor.total_years_exp) {
      return `${tutor.total_years_exp} years`;
    }
    return "Experience not specified";
  };

  // Helper function to get tutor's location
  const getTutorLocation = (tutor: Tutor) => {
    if (tutor.timezone) {
      return `Remote / ${tutor.timezone}`;
    }
    return "Remote";
  };

  // Helper function to get tutor's availability days
  const getTutorAvailability = (tutor: Tutor) => {
    if (!tutor.availability_slots || tutor.availability_slots.length === 0) {
      return ["Contact for availability"];
    }
    
    const dayNames = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
    const availableDays = Array.from(
      new Set(tutor.availability_slots.map(slot => dayNames[slot.day_of_week]))
    );
    
    return availableDays.length > 0 ? availableDays : ["Contact for availability"];
  };

  // Helper function to get tutor's profile picture
  const getTutorPhoto = (tutor: Tutor) => {
    return tutor.profile_picture_url || `https://ui-avatars.com/api/?name=${encodeURIComponent(getTutorName(tutor))}&background=6366f1&color=fff&size=256`;
  };

  
  // Helper function to get subjects for badge display with remaining count
  const getSubjectsForBadges = (tutor: Tutor) => {
    const subjects = tutor.subjects_taught || [];
    const expertise = tutor.expertise || [];
    const allSubjects = [...subjects, ...expertise];
    
    if (allSubjects.length <= 3) {
      return { displayedSubjects: allSubjects, remainingCount: 0 };
    }
    
    const displayedSubjects = allSubjects.slice(0, 3);
    const remainingCount = allSubjects.length - 3;
    return { displayedSubjects, remainingCount };
  };

  // Helper function to create initials fallback
  const getTutorInitials = (tutor: Tutor) => {
    const name = getTutorName(tutor);
    return name.split(' ').map(n => n[0]).join('').toUpperCase();
  };

  return (
    <div className="min-h-screen flex flex-col">
      <main className="flex-grow px-4 py-8 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="mb-5">
            <div className="flex items-start justify-between gap-6">
              {/* Left side - Title and Description */}
              <div className="flex-1">
                <h1 className="mb-2 text-4xl font-bold text-gray-900">
                  Find Your Perfect Tutor
                </h1>
                <p className="text-lg text-gray-600">
                  Connect with expert tutors specialized in STEM
                </p>
              </div>

              {/* Right side - Search Section */}
              <div className="flex-1 max-w-2xl">
                <div className="rounded-lg border border-gray-200 bg-white p-4 shadow-sm">
                  <div className="space-y-3">
                    {/* Search input */}
                    <div className="relative">
                      <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                        <Search className="h-5 w-5 text-gray-400" />
                      </div>
                      <Input
                        type="text"
                        placeholder="Search by name, specialty or keyword..."
                        className="h-10 pl-10 text-sm"
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                      />
                    </div>

                    {/* Filters and actions share one row when the search panel has room. */}
                    <div className="grid gap-3 xl:grid-cols-[minmax(0,1fr)_minmax(0,1fr)_auto]">
                      {/* Specialty filter */}
                      <Select value={specialty} onValueChange={setSpecialty}>
                        <SelectTrigger className="h-10">
                          <SelectValue placeholder="All Specialties" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="all">All Specialties</SelectItem>
                          {uniqueSpecialties.map((specialtyOption) => (
                            <SelectItem key={specialtyOption} value={specialtyOption}>
                              {specialtyOption}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>

                      {/* Availability filter */}
                      <Select value={availability} onValueChange={setAvailability}>
                        <SelectTrigger className="h-10">
                          <SelectValue placeholder="Any Day" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="any">Any Day</SelectItem>
                          <SelectItem value="Mon">Monday</SelectItem>
                          <SelectItem value="Tue">Tuesday</SelectItem>
                          <SelectItem value="Wed">Wednesday</SelectItem>
                          <SelectItem value="Thu">Thursday</SelectItem>
                          <SelectItem value="Fri">Friday</SelectItem>
                          <SelectItem value="Sat">Saturday</SelectItem>
                          <SelectItem value="Sun">Sunday</SelectItem>
                        </SelectContent>
                      </Select>
                      <div className="flex gap-3 xl:justify-self-end">
                        <Button
                          variant="outline"
                          onClick={() => {
                            setSearchTerm("");
                            setSpecialty("all");
                            setAvailability("any");
                          }}
                          className="h-10 px-4"
                        >
                          Reset Filters
                        </Button>
                        <Button className="h-10 bg-purple-600 px-4 text-white hover:bg-purple-700">
                          Apply Filters
                        </Button>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <Tabs defaultValue="grid" className="mb-8">
            <div className="flex justify-between items-center">
              <div className="text-sm text-gray-500">
                {isLoading ? "Loading..." : `${tutors.length} tutors found`}
              </div>
              <TabsList>
                <TabsTrigger value="grid">Grid View</TabsTrigger>
                <TabsTrigger value="list">List View</TabsTrigger>
              </TabsList>
            </div>

            <TabsContent value="grid" className="mt-6">
              {isLoading ? (
                <div className="flex justify-center items-center py-12">
                  <Loader2 className="h-8 w-8 animate-spin text-rfpurple-600" />
                  <span className="ml-2 text-gray-600">Loading tutors...</span>
                </div>
              ) : error ? (
                <div className="text-center py-12 bg-white rounded-lg shadow">
                  <h3 className="text-lg font-medium text-red-600">
                    Error loading tutors
                  </h3>
                  <p className="mt-2 text-gray-500">
                    Please try again later or contact support if the problem persists.
                  </p>
                </div>
              ) : tutors.length === 0 ? (
                <div className="text-center py-12 bg-white rounded-lg shadow">
                  <h3 className="text-lg font-medium text-gray-900">
                    No tutors found
                  </h3>
                  <p className="mt-2 text-gray-500">
                    Try adjusting your search filters to find available tutors.
                  </p>
                </div>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                  {tutors.map((tutor) => (
                    <Card
                      key={tutor.id}
                      className="overflow-hidden hover:shadow-lg transition-shadow duration-300"
                    >
                      <div className="flex flex-col h-full">
                        <div className="flex items-center p-6 pb-4">
                          <TooltipProvider>
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <div 
                                  className="w-16 h-16 mr-4 cursor-pointer hover:ring-2 hover:ring-rfpurple-300 rounded-full overflow-hidden"
                                  onClick={() => handleOpenTutorProfile(tutor)}
                                >
                                  <AuthenticatedImage
                                    src={getTutorPhoto(tutor)}
                                    alt={getTutorName(tutor)}
                                    className="w-16 h-16 rounded-full object-cover"
                                    fallback={
                                      <div className="w-16 h-16 rounded-full bg-rfpurple-600 flex items-center justify-center text-white font-medium text-sm">
                                        {getTutorInitials(tutor)}
                                      </div>
                                    }
                                  />
                                </div>
                              </TooltipTrigger>
                              <TooltipContent>View full profile</TooltipContent>
                            </Tooltip>
                          </TooltipProvider>
                          <div>
                            <TooltipProvider>
                              <Tooltip>
                                <TooltipTrigger asChild>
                                  <CardTitle
                                    className="text-lg cursor-pointer hover:text-rfpurple-600 transition-colors"
                                    onClick={() =>
                                      handleOpenTutorProfile(tutor)
                                    }
                                  >
                                    {getTutorName(tutor)}
                                  </CardTitle>
                                </TooltipTrigger>
                                <TooltipContent>
                                  View full profile
                                </TooltipContent>
                              </Tooltip>
                            </TooltipProvider>
                            <div className="flex items-center mt-1">
                              <Star className="h-4 w-4 text-yellow-400 fill-yellow-400" />
                              <span className="ml-1 text-sm text-gray-500">
                                {tutor.rating ? `${tutor.rating.toFixed(1)} ` : "No rating yet"}
                              </span>
                            </div>
                          </div>
                        </div>

                        <CardContent className="flex flex-col justify-between flex-grow">
                          <div>
                            <div className="flex flex-wrap gap-2 mb-4">
                              {(() => {
                                const { displayedSubjects, remainingCount } = getSubjectsForBadges(tutor);
                                return (
                                  <>
                                    {displayedSubjects.map((specialtyItem, index) => (
                                      <Badge
                                        key={`${specialtyItem}-${index}`}
                                        variant="secondary"
                                        className="bg-gray-100 text-gray-700"
                                      >
                                        {specialtyItem}
                                      </Badge>
                                    ))}
                                    {remainingCount > 0 && (
                                      <Badge
                                        variant="secondary"
                                        className="bg-rfpurple-100 text-rfpurple-700"
                                      >
                                        +{remainingCount} others
                                      </Badge>
                                    )}
                                  </>
                                );
                              })()}
                            </div>

                            <div className="space-y-2 mb-4">
                              <div className="flex items-center text-sm">
                                <Clock className="h-4 w-4 mr-2 text-gray-500" />
                                <span className="text-gray-700">{getTutorExperience(tutor)}</span>
                              </div>
                              <div className="flex items-center text-sm">
                                <MapPin className="h-4 w-4 mr-2 text-gray-500" />
                                <span className="text-gray-700">{getTutorLocation(tutor)}</span>
                              </div>
                              <div className="flex items-center text-sm">
                                <Calendar className="h-4 w-4 mr-2 text-gray-500" />
                                <span className="text-gray-700">
                                  Available: {getTutorAvailability(tutor).join(", ")}
                                </span>
                              </div>
                              <div className="flex items-center text-sm">
                                <BookOpen className="h-4 w-4 mr-2 text-gray-500" />
                                <span className="text-gray-700">{getTutorEducation(tutor)}</span>
                              </div>
                            </div>

                            {tutor.bio && (
                              <p className="text-sm text-gray-600 mb-4 line-clamp-2">{tutor.bio}</p>
                            )}
                          </div>

                          {/* Footer section pinned to the bottom */}
                          <div className="flex items-center justify-between pt-4 mt-auto border-t border-gray-100">
                            <Button className="button-gradient text-white" asChild>
                              <Link to={`/book-session/${tutor.id}`}>Book Session</Link>
                            </Button>
                          </div>
                        </CardContent>
                      </div>
                    </Card>
                  ))}
                </div>
              )}
            </TabsContent>

            <TabsContent value="list" className="mt-6">
              {isLoading ? (
                <div className="flex justify-center items-center py-12">
                  <Loader2 className="h-8 w-8 animate-spin text-rfpurple-600" />
                  <span className="ml-2 text-gray-600">Loading tutors...</span>
                </div>
              ) : error ? (
                <div className="text-center py-12 bg-white rounded-lg shadow">
                  <h3 className="text-lg font-medium text-red-600">
                    Error loading tutors
                  </h3>
                  <p className="mt-2 text-gray-500">
                    Please try again later or contact support if the problem persists.
                  </p>
                </div>
              ) : tutors.length === 0 ? (
                <div className="text-center py-12 bg-white rounded-lg shadow">
                  <h3 className="text-lg font-medium text-gray-900">
                    No tutors found
                  </h3>
                  <p className="mt-2 text-gray-500">
                    Try adjusting your search filters to find available tutors.
                  </p>
                </div>
              ) : (
                <div className="space-y-4">
                  {tutors.map((tutor) => (
                    <Card
                      key={tutor.id}
                      className="overflow-hidden hover:shadow-lg transition-shadow duration-300 flex flex-col h-full"
                    >
                      <div className="p-6">
                        <div className="flex flex-col md:flex-row md:items-center">
                          <div className="flex items-center mb-4 md:mb-0 md:mr-6">
                            <TooltipProvider>
                              <Tooltip>
                                <TooltipTrigger asChild>
                                  <div 
                                    className="w-16 h-16 mr-4 cursor-pointer hover:ring-2 hover:ring-rfpurple-300 rounded-full overflow-hidden"
                                    onClick={() => handleOpenTutorProfile(tutor)}
                                  >
                                    <AuthenticatedImage
                                      src={getTutorPhoto(tutor)}
                                      alt={getTutorName(tutor)}
                                      className="w-16 h-16 rounded-full object-cover"
                                      fallback={
                                        <div className="w-16 h-16 rounded-full bg-rfpurple-600 flex items-center justify-center text-white font-medium text-sm">
                                          {getTutorInitials(tutor)}
                                        </div>
                                      }
                                    />
                                  </div>
                                </TooltipTrigger>
                                <TooltipContent>
                                  View full profile
                                </TooltipContent>
                              </Tooltip>
                            </TooltipProvider>
                            <div>
                              <TooltipProvider>
                                <Tooltip>
                                  <TooltipTrigger asChild>
                                    <h3
                                      className="text-lg font-semibold cursor-pointer hover:text-rfpurple-600 transition-colors"
                                      onClick={() =>
                                        handleOpenTutorProfile(tutor)
                                      }
                                    >
                                      {getTutorName(tutor)}
                                    </h3>
                                  </TooltipTrigger>
                                  <TooltipContent>
                                    View full profile
                                  </TooltipContent>
                                </Tooltip>
                              </TooltipProvider>
                              <div className="flex items-center mt-1">
                                <Star className="h-4 w-4 text-yellow-400 fill-yellow-400" />
                                <span className="ml-1 text-sm text-gray-500">
                                  {tutor.rating ? `${tutor.rating.toFixed(1)} ` : "No rating yet"}
                                </span>
                              </div>
                            </div>
                          </div>

                          <div className="flex-grow space-y-2 mb-4 md:mb-0">
                            <div className="flex flex-wrap gap-2 mb-2">
                              {(() => {
                                const { displayedSubjects, remainingCount } = getSubjectsForBadges(tutor);
                                return (
                                  <>
                                    {displayedSubjects.map((specialtyItem, index) => (
                                      <Badge
                                        key={`${specialtyItem}-${index}`}
                                        variant="secondary"
                                        className="bg-gray-100 text-gray-700"
                                      >
                                        {specialtyItem}
                                      </Badge>
                                    ))}
                                    {remainingCount > 0 && (
                                      <Badge
                                        variant="secondary"
                                        className="bg-rfpurple-100 text-rfpurple-700"
                                      >
                                        +{remainingCount} others
                                      </Badge>
                                    )}
                                  </>
                                );
                              })()}
                            </div>
                            {tutor.bio && (
                              <p className="text-sm text-gray-600 line-clamp-2">
                                {tutor.bio}
                              </p>
                            )}
                            <div className="flex flex-wrap gap-4 text-sm">
                              <div className="flex items-center">
                                <Clock className="h-4 w-4 mr-1 text-gray-500" />
                                <span>{getTutorExperience(tutor)}</span>
                              </div>
                              <div className="flex items-center">
                                <MapPin className="h-4 w-4 mr-1 text-gray-500" />
                                <span>{getTutorLocation(tutor)}</span>
                              </div>
                              <div className="flex items-center">
                                <Calendar className="h-4 w-4 mr-1 text-gray-500" />
                                <span>
                                  Available: {getTutorAvailability(tutor).join(", ")}
                                </span>
                              </div>
                              <div className="flex items-center">
                                <BookOpen className="h-4 w-4 mr-1 text-gray-500" />
                                <span>{getTutorEducation(tutor)}</span>
                              </div>
                            </div>
                          </div>

                          <div className="flex items-center justify-between md:justify-end md:ml-4">
                            <Button
                              className="button-gradient text-white"
                              asChild
                            >
                              <Link to={`/book-session/${tutor.id}`}>
                                Book Session
                              </Link>
                            </Button>
                          </div>
                        </div>
                      </div>
                    </Card>
                  ))}
                </div>
              )}
            </TabsContent>
          </Tabs>
        </div>
      </main>

      {/* Add the TutorProfileModal component */}
      {selectedTutor && (
        <TutorProfileModal
          tutor={selectedTutor}
          isOpen={showProfileModal}
          onClose={handleCloseModal}
        />
      )}
    </div>
  );
};

export default TutorSearch;
